import React from "react";
import { Helmet } from "react-helmet";
import {
  FiArrowRight,
  FiCheck,
  FiBarChart2,
  FiShield,
  FiTrendingUp,
  FiFileText,
  FiCreditCard,
  FiUsers,
  FiClock,
  FiDatabase,
  FiAlertCircle,
  FiZap,
  FiPieChart,
  FiActivity,
} from "react-icons/fi";

import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import "./Finance.css";

const Finance = () => {
  return (
    <>
      <Helmet>
        <title>AI Solutions for Finance | Dropgear IO</title>
        <meta
          name="description"
          content="Dropgear IO helps finance teams automate repetitive workflows, monitor financial operations, process documents, and build intelligent AI-powered systems."
        />
      </Helmet>

      <Header />

      <main className="finance-page">

        {/* =====================================================
            HERO
        ===================================================== */}
        <section className="finance-hero">
          <div className="finance-container finance-hero-grid">

            <div className="finance-hero-content">
              <span className="finance-eyebrow">
                AI FOR FINANCE
              </span>

              <h1>
                Intelligent systems for
                <span> modern finance.</span>
              </h1>

              <p>
                Dropgear IO helps finance teams automate operational
                workflows, organize financial information, reduce repetitive
                work, and turn everyday business activity into actionable
                intelligence.
              </p>

              <div className="finance-hero-actions">
                <a href="/book-demo" className="finance-btn-primary">
                  Book a Demo
                  <FiArrowRight />
                </a>

                <a href="#finance-use-cases" className="finance-btn-secondary">
                  Explore Solutions
                </a>
              </div>

              <div className="finance-hero-points">
                <span>
                  <FiCheck />
                  Workflow automation
                </span>

                <span>
                  <FiCheck />
                  Financial intelligence
                </span>

                <span>
                  <FiCheck />
                  Human oversight
                </span>
              </div>
            </div>

            {/* DASHBOARD */}
            <div className="finance-dashboard-wrapper">

              <div className="finance-dashboard">

                <div className="finance-dashboard-top">
                  <div>
                    <span>Finance Operations</span>
                    <strong>AI Command Center</strong>
                  </div>

                  <div className="finance-live">
                    <i></i>
                    Live
                  </div>
                </div>

                <div className="finance-dashboard-summary">

                  <div className="finance-summary-card">
                    <div className="finance-summary-icon">
                      <FiTrendingUp />
                    </div>

                    <span>Operational Flow</span>
                    <strong>92.4%</strong>

                    <small>
                      <FiTrendingUp />
                      8.7% this month
                    </small>
                  </div>

                  <div className="finance-summary-card">
                    <div className="finance-summary-icon">
                      <FiFileText />
                    </div>

                    <span>Documents Processed</span>
                    <strong>1,284</strong>

                    <small>
                      <FiZap />
                      AI assisted
                    </small>
                  </div>

                </div>

                <div className="finance-chart-card">

                  <div className="finance-chart-header">
                    <div>
                      <span>Workflow Activity</span>
                      <strong>Financial Operations</strong>
                    </div>

                    <FiActivity />
                  </div>

                  <div className="finance-chart">
                    <div className="finance-chart-line">
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>

                  <div className="finance-chart-labels">
                    <span>Mon</span>
                    <span>Tue</span>
                    <span>Wed</span>
                    <span>Thu</span>
                    <span>Fri</span>
                    <span>Sat</span>
                    <span>Sun</span>
                  </div>

                </div>

                <div className="finance-dashboard-bottom">

                  <div className="finance-operation">
                    <span className="finance-operation-icon">
                      <FiFileText />
                    </span>

                    <div>
                      <strong>Invoice Processing</strong>
                      <small>Completed automatically</small>
                    </div>

                    <b>98%</b>
                  </div>

                  <div className="finance-operation">
                    <span className="finance-operation-icon">
                      <FiAlertCircle />
                    </span>

                    <div>
                      <strong>Exceptions</strong>
                      <small>Needs review</small>
                    </div>

                    <b>12</b>
                  </div>

                </div>

              </div>

            </div>

          </div>
        </section>


        {/* =====================================================
            INTRO
        ===================================================== */}
        <section className="finance-intro">
          <div className="finance-container">

            <div className="finance-section-heading">
              <span className="finance-eyebrow">
                FINANCE OPERATIONS
              </span>

              <h2>
                Move financial work from
                <span> repetitive to intelligent.</span>
              </h2>

              <p>
                Finance teams manage large volumes of documents, transactions,
                approvals, reports, and operational requests. Dropgear IO
                connects these workflows with intelligent automation so teams
                can spend more time making decisions and less time moving
                information between systems.
              </p>
            </div>

          </div>
        </section>


        {/* =====================================================
            USE CASES
        ===================================================== */}
        <section
          className="finance-use-cases"
          id="finance-use-cases"
        >
          <div className="finance-container">

            <div className="finance-section-heading centered">
              <span className="finance-eyebrow">
                AI CAPABILITIES
              </span>

              <h2>
                AI that works across
                <span> financial operations.</span>
              </h2>

              <p>
                Build intelligent workflows around the processes your finance
                team already uses.
              </p>
            </div>

            <div className="finance-use-grid">

              <div className="finance-use-card">
                <div className="finance-card-icon">
                  <FiFileText />
                </div>

                <h3>Document Intelligence</h3>

                <p>
                  Extract and organize useful information from invoices,
                  financial documents, statements, and operational records.
                </p>

                <ul>
                  <li>
                    <FiCheck />
                    Document extraction
                  </li>
                  <li>
                    <FiCheck />
                    Data classification
                  </li>
                  <li>
                    <FiCheck />
                    Information routing
                  </li>
                </ul>
              </div>


              <div className="finance-use-card">
                <div className="finance-card-icon">
                  <FiCreditCard />
                </div>

                <h3>Invoice Operations</h3>

                <p>
                  Automate repetitive invoice workflows while keeping people
                  involved when an exception requires attention.
                </p>

                <ul>
                  <li>
                    <FiCheck />
                    Invoice capture
                  </li>
                  <li>
                    <FiCheck />
                    Validation workflows
                  </li>
                  <li>
                    <FiCheck />
                    Approval routing
                  </li>
                </ul>
              </div>


              <div className="finance-use-card">
                <div className="finance-card-icon">
                  <FiBarChart2 />
                </div>

                <h3>Financial Intelligence</h3>

                <p>
                  Bring operational data together and create intelligent views
                  that help teams understand what is happening across the
                  business.
                </p>

                <ul>
                  <li>
                    <FiCheck />
                    Operational analytics
                  </li>
                  <li>
                    <FiCheck />
                    Trend monitoring
                  </li>
                  <li>
                    <FiCheck />
                    Decision support
                  </li>
                </ul>
              </div>


              <div className="finance-use-card">
                <div className="finance-card-icon">
                  <FiAlertCircle />
                </div>

                <h3>Exception Detection</h3>

                <p>
                  Identify unusual cases and route them to the right person
                  instead of allowing exceptions to disappear inside manual
                  processes.
                </p>

                <ul>
                  <li>
                    <FiCheck />
                    Rule-based monitoring
                  </li>
                  <li>
                    <FiCheck />
                    Exception routing
                  </li>
                  <li>
                    <FiCheck />
                    Human review
                  </li>
                </ul>
              </div>


              <div className="finance-use-card">
                <div className="finance-card-icon">
                  <FiUsers />
                </div>

                <h3>Finance Assistants</h3>

                <p>
                  Give finance teams AI assistants that can help find
                  information, summarize records, and support everyday
                  operational questions.
                </p>

                <ul>
                  <li>
                    <FiCheck />
                    Natural language queries
                  </li>
                  <li>
                    <FiCheck />
                    Report summaries
                  </li>
                  <li>
                    <FiCheck />
                    Workflow assistance
                  </li>
                </ul>
              </div>


              <div className="finance-use-card">
                <div className="finance-card-icon">
                  <FiPieChart />
                </div>

                <h3>Management Reporting</h3>

                <p>
                  Turn operational information into structured summaries that
                  help managers understand performance and emerging issues.
                </p>

                <ul>
                  <li>
                    <FiCheck />
                    Automated summaries
                  </li>
                  <li>
                    <FiCheck />
                    Performance views
                  </li>
                  <li>
                    <FiCheck />
                    Decision support
                  </li>
                </ul>
              </div>

            </div>
          </div>
        </section>


        {/* =====================================================
            PLATFORM
        ===================================================== */}
        <section className="finance-platform">
          <div className="finance-container finance-platform-grid">

            <div className="finance-platform-visual">

              <div className="finance-platform-window">

                <div className="finance-window-header">
                  <span>Dropgear Finance Workspace</span>

                  <div>
                    <i></i>
                    <i></i>
                    <i></i>
                  </div>
                </div>

                <div className="finance-window-body">

                  <div className="finance-workflow-title">
                    <span>Today's workflow</span>
                    <strong>Operations Overview</strong>
                  </div>

                  <div className="finance-progress">

                    <div className="finance-progress-item">
                      <div>
                        <span>Invoices</span>
                        <b>84%</b>
                      </div>

                      <div className="finance-progress-track">
                        <span style={{ width: "84%" }}></span>
                      </div>
                    </div>

                    <div className="finance-progress-item">
                      <div>
                        <span>Approvals</span>
                        <b>67%</b>
                      </div>

                      <div className="finance-progress-track">
                        <span style={{ width: "67%" }}></span>
                      </div>
                    </div>

                    <div className="finance-progress-item">
                      <div>
                        <span>Reconciliation</span>
                        <b>91%</b>
                      </div>

                      <div className="finance-progress-track">
                        <span style={{ width: "91%" }}></span>
                      </div>
                    </div>

                  </div>

                  <div className="finance-task-list">

                    <div>
                      <span>
                        <FiCheck />
                      </span>

                      <p>
                        Invoice batch processed
                        <small>AI workflow completed</small>
                      </p>

                      <b>Done</b>
                    </div>

                    <div>
                      <span>
                        <FiClock />
                      </span>

                      <p>
                        Approval exception
                        <small>Waiting for review</small>
                      </p>

                      <b>Review</b>
                    </div>

                    <div>
                      <span>
                        <FiDatabase />
                      </span>

                      <p>
                        Financial data sync
                        <small>System connected</small>
                      </p>

                      <b>Active</b>
                    </div>

                  </div>

                </div>

              </div>

            </div>


            <div className="finance-platform-content">

              <span className="finance-eyebrow">
                ONE INTELLIGENT LAYER
              </span>

              <h2>
                Connect your financial
                <span> workflows in one place.</span>
              </h2>

              <p>
                Dropgear IO can sit alongside the tools your team already uses,
                helping coordinate information and workflows without forcing
                your organization to replace everything at once.
              </p>

              <div className="finance-platform-list">

                <div>
                  <span>
                    <FiZap />
                  </span>

                  <div>
                    <h3>Automate repetitive work</h3>
                    <p>
                      Reduce manual data movement and repetitive operational
                      steps.
                    </p>
                  </div>
                </div>

                <div>
                  <span>
                    <FiDatabase />
                  </span>

                  <div>
                    <h3>Connect existing systems</h3>
                    <p>
                      Bring information together across your operational
                      environment.
                    </p>
                  </div>
                </div>

                <div>
                  <span>
                    <FiUsers />
                  </span>

                  <div>
                    <h3>Keep people in control</h3>
                    <p>
                      Route important decisions and exceptions to the right
                      people.
                    </p>
                  </div>
                </div>

              </div>

            </div>

          </div>
        </section>


        {/* =====================================================
            HOW IT WORKS
        ===================================================== */}
        <section className="finance-how">
          <div className="finance-container">

            <div className="finance-section-heading centered">

              <span className="finance-eyebrow">
                HOW IT WORKS
              </span>

              <h2>
                From financial workflow
                <span> to intelligent action.</span>
              </h2>

              <p>
                Dropgear IO is designed around practical implementation rather
                than forcing finance teams into complicated transformation
                projects.
              </p>

            </div>


            <div className="finance-steps">

              <div className="finance-step">
                <div className="finance-step-number">01</div>

                <div className="finance-step-icon">
                  <FiDatabase />
                </div>

                <h3>Connect</h3>

                <p>
                  Bring relevant systems, documents, and operational data into
                  the workflow.
                </p>
              </div>


              <div className="finance-step">
                <div className="finance-step-number">02</div>

                <div className="finance-step-icon">
                  <FiZap />
                </div>

                <h3>Automate</h3>

                <p>
                  Define repeatable processes that AI can assist, accelerate,
                  or execute.
                </p>
              </div>


              <div className="finance-step">
                <div className="finance-step-number">03</div>

                <div className="finance-step-icon">
                  <FiActivity />
                </div>

                <h3>Monitor</h3>

                <p>
                  Track workflow activity and identify exceptions that require
                  human attention.
                </p>
              </div>


              <div className="finance-step">
                <div className="finance-step-number">04</div>

                <div className="finance-step-icon">
                  <FiTrendingUp />
                </div>

                <h3>Improve</h3>

                <p>
                  Continuously refine workflows using operational insights and
                  real-world feedback.
                </p>
              </div>

            </div>

          </div>
        </section>


        {/* =====================================================
            BENEFITS
        ===================================================== */}
        <section className="finance-benefits">

          <div className="finance-container finance-benefits-grid">

            <div className="finance-benefits-content">

              <span className="finance-eyebrow">
                BUILT FOR FINANCE TEAMS
              </span>

              <h2>
                Better operations without
                <span> adding more complexity.</span>
              </h2>

              <p>
                AI should simplify financial operations, not create another
                system that your team has to manage. Dropgear IO focuses on
                practical workflows that can evolve with your organization.
              </p>

              <div className="finance-benefit-list">

                <div>
                  <FiCheck />
                  <span>Reduce repetitive manual tasks</span>
                </div>

                <div>
                  <FiCheck />
                  <span>Improve workflow visibility</span>
                </div>

                <div>
                  <FiCheck />
                  <span>Surface exceptions earlier</span>
                </div>

                <div>
                  <FiCheck />
                  <span>Support faster operational decisions</span>
                </div>

                <div>
                  <FiCheck />
                  <span>Keep human review where it matters</span>
                </div>

              </div>

            </div>


            <div className="finance-benefit-card">

              <div className="finance-benefit-card-top">
                <span>
                  <FiBarChart2 />
                </span>

                <div>
                  <small>Operational Intelligence</small>
                  <strong>Finance Overview</strong>
                </div>
              </div>

              <div className="finance-metric">

                <div>
                  <span>Tasks automated</span>
                  <strong>76%</strong>
                </div>

                <div className="finance-metric-bar">
                  <span style={{ width: "76%" }}></span>
                </div>

              </div>

              <div className="finance-metric">

                <div>
                  <span>Exception visibility</span>
                  <strong>89%</strong>
                </div>

                <div className="finance-metric-bar">
                  <span style={{ width: "89%" }}></span>
                </div>

              </div>

              <div className="finance-metric">

                <div>
                  <span>Workflow coverage</span>
                  <strong>81%</strong>
                </div>

                <div className="finance-metric-bar">
                  <span style={{ width: "81%" }}></span>
                </div>

              </div>

              <div className="finance-card-note">
                <FiShield />
                Designed around controlled workflows and human oversight.
              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            SECURITY
        ===================================================== */}
        <section className="finance-security">

          <div className="finance-container finance-security-box">

            <div className="finance-security-icon">
              <FiShield />
            </div>

            <div className="finance-security-content">

              <span className="finance-eyebrow">
                SECURITY & CONTROL
              </span>

              <h2>
                Built with responsible
                <span> financial automation in mind.</span>
              </h2>

              <p>
                Financial workflows require visibility, control, and careful
                handling of information. Dropgear IO is designed to support
                controlled automation with configurable workflows and human
                review where needed.
              </p>

              <div className="finance-security-points">

                <span>
                  <FiCheck />
                  Controlled workflow execution
                </span>

                <span>
                  <FiCheck />
                  Human review for exceptions
                </span>

                <span>
                  <FiCheck />
                  Clear operational visibility
                </span>

                <span>
                  <FiCheck />
                  Configurable access and processes
                </span>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            CTA
        ===================================================== */}
        <section className="finance-cta">

          <div className="finance-container">

            <div className="finance-cta-box">

              <div>
                <span className="finance-eyebrow">
                  READY TO EXPLORE?
                </span>

                <h2>
                  Make your finance operations
                  <span> work smarter.</span>
                </h2>

                <p>
                  Explore how Dropgear IO can help turn repetitive finance
                  workflows into intelligent, measurable operations.
                </p>
              </div>

              <a href="/contact" className="finance-btn-primary">
                Book a Demo
                <FiArrowRight />
              </a>

            </div>

          </div>

        </section>

      </main>

      <Footer />
    </>
  );
};

export default Finance;